<template>
    <v-dialog v-model="shows" max-width="820px" persistent>
        <template #activator="{ attrs, on }">
            <slot name="activator" v-bind="{ attrs, on }" />
        </template>
        <v-card v-if="shows" v-bind="{ loading }">
            <v-card-title>
                <span v-if="isCreate" class="subtitle-1">공지사항 등록</span>
                <span v-else class="subtitle-1">공지사항 수정</span>
            </v-card-title>
            <v-card-text>
                <v-row class="mt-3">
                    <v-col class="py-0">
                        <v-text-field v-model="form.subject" label="제목" persistent-placeholder hide-details />
                    </v-col>
                    <v-col class="py-0">
                        <v-datetime-field v-model="form.createdAt" label="생성일" type="date" persistent-placeholder hide-details />
                    </v-col>
                </v-row>
                <file-chips-field v-model="form.files" label="첨부파일" persistent-placeholder hide-details class="mt-4" />
                <div class="caption mt-4 mb-1">내용</div>
                <naver-smarteditor v-model="form.content" rows="10" />
            </v-card-text>

            <v-card-actions>
                <v-switch v-model="form.isNotice" class="ma-0 py-0 px-4" hide-details>
                    <span slot="label" class="subtitle-2">상단고정</span>
                </v-switch>
                <v-spacer />
                <v-btn text v-bind="{ loading }" @click="shows = false">취소</v-btn>
                <v-btn color="primary" text v-bind="{ loading }" @click="save">저장</v-btn>
            </v-card-actions>
            <v-fade-transition>
                <v-overlay v-show="loading" light color="white">
                    <v-progress-circular indeterminate width="5" size="100" color="primary" />
                </v-overlay>
            </v-fade-transition>
        </v-card>
    </v-dialog>
</template>

<script>
import api from "@/api";
import { initCenterNotification } from "@/assets/variables";

import FileChipsField from "@/components/console/dumb/file-chips-field.vue";
import VDatetimeField from "@/components/plugins/vuetify/v-datetime-field.vue";
import NaverSmarteditor from "@/components/plugins/naver/naver-smarteditor.vue";

// 옛 단일 첨부(upload)는 칩 하나로 보여 준다. 새 첨부(common-files)로 옮기지 않는다
const toUploadChip = (upload) => ({ isUpload: true, url: upload, name: upload.split("/").pop() });

export default {
    components: {
        FileChipsField,
        VDatetimeField,
        NaverSmarteditor,
    },
    props: {
        value: { type: Object, default: null }, // 수정할 공지. 없으면 등록
        site: { type: String, default: null }, // 등록할 때 붙일 사이트
    },
    data: () => ({
        form: initCenterNotification(),

        shows: false,
        loading: false,

        // 저장이 중간에 실패했지만 공지는 이미 바뀐 상태. 창을 닫을 때 목록을 다시 읽게 한다
        changed: null,
    }),
    computed: {
        isCreate() {
            return !this.form._id;
        },
    },
    watch: {
        shows() {
            if (this.shows) this.init();
            else if (this.changed) {
                // 열려 있는 동안 목록을 다시 읽으면 이 폼이 든 행이 다른 공지로 바뀌거나 사라질 수 있어 닫을 때 한다
                this.$emit("input", this.changed);
                this.changed = null;
            }
        },
    },
    methods: {
        init() {
            const form = initCenterNotification(this.value || {});
            if (form.upload) form.files = [toUploadChip(form.upload), ...form.files];
            this.form = form;
        },

        // 첨부 저장 순서: 새 파일 올리기 → 첨부 목록 저장 → 뺀 첨부 지우기
        // 중간에 실패해도 기존 첨부는 남는다 (먼저 지우면 업로드 실패 때 첨부가 사라진다)
        async save() {
            if (this.loading) return;
            else this.loading = true;

            try {
                const { post, put } = api.console.center.notifications;
                // viewCount 는 폼을 연 뒤에도 늘어나므로 보내지 않는다
                const { _files: savedFiles, files, upload, viewCount, ...fields } = this.form;

                if (!files.some((file) => file.isUpload)) fields.upload = null;

                // 1. 공지 저장. 첨부가 실패한 뒤 다시 저장해도 공지가 둘이 되지 않게 _id 를 바로 잡아 둔다
                const { notification } = await (this.isCreate ? post({ ...fields, sites: this.site ? [this.site] : [] }) : put(fields));
                // 다시 저장하면 PUT 으로 가므로 서버가 정한 사이트도 받아 둔다(빈 배열로 덮지 않게)
                this.form._id = notification._id;
                this.form.sites = notification.sites;
                this.changed = notification;

                // 2. 새 파일만 올린다. 성공한 것은 바로 문서로 바꿔 다시 저장할 때 또 올리지 않는다
                //    폴더 이름을 매번 새로 붙여 뺀 첨부와 경로가 겹치지 않게 한다
                const stamp = Date.now();
                for (const [index, file] of files.entries()) {
                    if (!(file instanceof File)) continue;
                    const { file: uploaded } = await api.console.common.files.post({ _notification: notification._id, index: `${stamp}-${index}` }, file);
                    this.form.files.splice(index, 1, uploaded);
                }

                // 3. 첨부 목록 저장
                const _files = this.form.files.filter((file) => !file.isUpload).map(({ _id }) => _id);
                await put({ _id: notification._id, _files });

                // 4. 뺀 첨부만 지운다. 중간에 실패하면 다시 저장할 때 남은 것을 이어서 지운다(없는 첨부 삭제도 성공으로 온다)
                for (const _id of savedFiles.filter((_id) => !_files.includes(_id))) {
                    await api.console.common.files.delete({ _id });
                }
                this.form._files = _files;

                this.changed = null;
                this.$emit("input", notification);
                this.shows = false;
            } catch (error) {
                console.error(error);
                // 403 은 axios 인터셉터가 이미 알린다 (@/plugins/axios.js)
                if (error.response?.status !== 403) alert("저장하지 못했습니다. 다시 시도해 주세요.");
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
